/**
 * The chapters a set of subjects can be examined on, for one class.
 *
 * Shared by the papers table and the paper dialog, which need the same answer
 * for different reasons: the dialog offers the chapters to pick, the table
 * names the ones that were picked. Resolving it twice would let the two drift
 * — a table saying "3 chapters" beside a dialog offering a different three.
 *
 * A subject's curriculum is found by the class's board and grade, which is what
 * `Curriculum` is keyed on. A subject with no curriculum simply contributes no
 * chapters: the paper then covers the whole syllabus, which is what an absent
 * scope already means.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { curriculumApi } from '../api/curriculumApi';
import type { CurriculumListItem } from '../types';

export interface ExamChapter {
  id: string;
  /** "3. Refraction of Light" — the number is part of how a chapter is named. */
  name: string;
}

export interface ExamChapterGroup {
  subject: string;
  chapters: ExamChapter[];
}

export interface UseExamChapters {
  groups: ExamChapterGroup[];
  /** Every chapter id across every subject, in display order. */
  allIds: string[];
  /** id → name, for rendering a stored scope back as words. */
  nameById: Map<string, string>;
  isLoading: boolean;
}

export function useExamChapters(
  subjects: string[], board?: string, grade?: string, enabled = true,
): UseExamChapters {
  /* Every curriculum, so a subject can be matched to one. Cached under a key
     the rest of the console shares, so this costs one request however many
     dialogs and tables ask. */
  const { data: curricula } = useQuery({
    queryKey: ['curricula'],
    queryFn: () => curriculumApi.listCurricula(),
    enabled,
  });

  const curriculumBySubject = useMemo(() => {
    const out = new Map<string, CurriculumListItem>();
    for (const s of subjects) {
      const match = (curricula?.curricula ?? []).find(c =>
        c.subject === s
        && (!board || String(c.board).toLowerCase() === String(board).toLowerCase())
        && (!grade || String(c.grade ?? '') === String(grade)));
      if (match) out.set(s, match);
    }
    return out;
  }, [curricula, subjects, board, grade]);

  const key = [...curriculumBySubject.entries()].map(([s, c]) => `${s}:${c.id}`).join('|');

  const tocs = useQuery({
    queryKey: ['exam-chapters', key],
    enabled: enabled && curriculumBySubject.size > 0,
    queryFn: async () => {
      const out: ExamChapterGroup[] = [];
      for (const [subject, curriculum] of curriculumBySubject) {
        const res = await curriculumApi.getCurriculumToc(curriculum.id, subject);
        out.push({
          subject,
          /* Chapters only — a TOC carries books and sections too, and a paper's
             scope is stated in chapters.

             Sorted by NUMBER read as a number. The TOC comes back in placement
             order, which showed 1, 4, 7, 8, 11, 12, 2, 5; a syllabus is the one
             list a reader knows the running order of. Lexical sorting would put
             10 before 2, `order` breaks a tie, and anything unnumbered sinks
             rather than jumping to the top. */
          chapters: (res.nodes ?? [])
            .filter(n => n.bookLevel === 'chapter')
            .slice()
            .sort((a, b) => {
              const na = Number.parseFloat(a.number ?? '');
              const nb = Number.parseFloat(b.number ?? '');
              if (Number.isNaN(na) && Number.isNaN(nb)) return (a.order ?? 0) - (b.order ?? 0);
              if (Number.isNaN(na)) return 1;
              if (Number.isNaN(nb)) return -1;
              return na - nb || (a.order ?? 0) - (b.order ?? 0);
            })
            .map(n => ({
              id: n.canonicalNodeId ?? n.id,
              name: `${n.number ? `${n.number}. ` : ''}${n.canonicalName ?? n.displayName}`,
            })),
        });
      }
      return out;
    },
  });

  const groups = useMemo(() => tocs.data ?? [], [tocs.data]);

  return useMemo(() => ({
    groups,
    allIds: groups.flatMap(g => g.chapters.map(c => c.id)),
    nameById: new Map(groups.flatMap(g => g.chapters.map(c => [c.id, c.name] as const))),
    isLoading: tocs.isLoading,
  }), [groups, tocs.isLoading]);
}
